import { Link, usePage } from '@inertiajs/react'
// Mantine's CSS, imported per component rather than as the whole bundle.
//
// `@mantine/core/styles.css` is 273KB of rules for ~100 components, of which
// this app renders 27. It is the page's ONLY render-blocking resource, and
// blocking a paint on it is expensive twice over: the bytes on the wire, and
// then the style recalculation (847ms of the station page's main thread).
//
// Measured on the mobile station page by serving truncated copies of the real
// stylesheet — same page, same throttling, ads held out:
//
//   231KB (all of it)   FCP/LCP 1512ms
//   113KB (half)        FCP/LCP  636ms
//    56KB (a quarter)   FCP/LCP  480ms
//
// FCP and LCP are the same number here: the LCP element is the server-rendered
// <h1>, so it paints as soon as anything does, and the stylesheet is what it
// waits for. Cutting the sheet is the whole LCP fix.
//
// The list below is every component imported from @mantine/core anywhere in
// app/javascript, plus the ones Mantine renders internally for them (Popover
// under Menu, ModalBase/Overlay/CloseButton under Modal, Input under TextInput,
// UnstyledButton under Button and ActionIcon, Loader inside both). Components
// with no entry here — Box, Collapse, TextInput itself — carry no stylesheet of
// their own.
//
// ORDER MATTERS: baseline, then variables, then global, then components. And
// adding a Mantine component to a page now means adding its CSS here; a missing
// line renders that one control unstyled, which is what the screenshot pass in
// the PR was for.
import '@mantine/core/styles/baseline.css'
import '@mantine/core/styles/default-css-variables.css'
import '@mantine/core/styles/global.css'
import '@mantine/core/styles/ActionIcon.css'
import '@mantine/core/styles/Anchor.css'
import '@mantine/core/styles/AppShell.css'
import '@mantine/core/styles/AspectRatio.css'
import '@mantine/core/styles/Badge.css'
import '@mantine/core/styles/Button.css'
import '@mantine/core/styles/Card.css'
import '@mantine/core/styles/Center.css'
import '@mantine/core/styles/CloseButton.css'
import '@mantine/core/styles/Container.css'
import '@mantine/core/styles/Flex.css'
import '@mantine/core/styles/Grid.css'
import '@mantine/core/styles/Group.css'
import '@mantine/core/styles/Input.css'
import '@mantine/core/styles/List.css'
import '@mantine/core/styles/Loader.css'
import '@mantine/core/styles/Menu.css'
import '@mantine/core/styles/Modal.css'
import '@mantine/core/styles/ModalBase.css'
import '@mantine/core/styles/Overlay.css'
import '@mantine/core/styles/Paper.css'
import '@mantine/core/styles/Popover.css'
import '@mantine/core/styles/ScrollArea.css'
import '@mantine/core/styles/SegmentedControl.css'
import '@mantine/core/styles/SimpleGrid.css'
import '@mantine/core/styles/Skeleton.css'
import '@mantine/core/styles/Stack.css'
import '@mantine/core/styles/Text.css'
import '@mantine/core/styles/Title.css'
import '@mantine/core/styles/UnstyledButton.css'
import '@mantine/core/styles/VisuallyHidden.css'
import './_skiplink.css'
import {
  MantineProvider,
  AppShell,
  Group,
  Anchor,
  ActionIcon,
  Text,
  Container,
  Box,
  Stack,
} from '@mantine/core'
import { useDisclosure, useHotkeys } from '@mantine/hooks'
import { LocaleProvider, useI18n } from './_i18n'
import { useLocalePath } from './_localePath'
import LanguageSwitcher from './_LanguageSwitcher'
import { UnitsProvider } from './_units'
import { StationsProvider } from './_stations'
import SearchOverlay from './_SearchOverlay'
import SettingsOverlay from './_SettingsOverlay'
import ErrorBoundary from './_ErrorBoundary'
import { theme } from './_theme'
import { storeUrl, storeBadge, trackStoreClick } from './_appStore'
import { ChromelessProvider, useChromeless } from './_chromeless'

// Dark brand navy for the top + bottom nav (always, in light and dark) — palette
// #05202d, a touch darker than the dark-mode body so the bars stay distinct.
const NAV_BG = '#05202d'
const NAV_BORDER = '1px solid rgba(255,255,255,0.08)'

// THE NOTCH, and why the site chrome has to know about it now.
//
// `viewport-fit=cover` was added to the viewport meta (layouts/application.html.erb)
// so ExploreMap's control inset can read `env(safe-area-inset-top)` — a
// maximized map is chromeless and fixed to the WHOLE viewport, so without the
// safe area its top button would sit under the status bar on a notched phone.
// But `cover` is a page-wide switch: it also lets the header run under that
// status bar, and lets both bars run under the cutout in landscape. Visible
// only where the insets are non-zero — an installed (display: standalone) app
// on a notched phone, and landscape in Safari — and zero everywhere else,
// which is why this is padding rather than a media query.
//
// The header's HEIGHT grows by the same amount it is padded, so the bar still
// shows 56 px of content and AppShell's own main-area offset follows it: the
// height feeds --app-shell-header-height, and the inner Group's `h="100%"`
// resolves against the content box, which border-box sizing keeps at 56.
const SAFE_TOP = 'env(safe-area-inset-top, 0px)'
const SAFE_BOTTOM = 'env(safe-area-inset-bottom, 0px)'
const SAFE_LEFT = 'env(safe-area-inset-left, 0px)'
const SAFE_RIGHT = 'env(safe-area-inset-right, 0px)'
const HEADER_CONTENT_H = 56

function GearIcon({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  )
}

function SearchIcon({ size = 19 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
}

// The visible chrome (rendered inside the providers below so it can translate):
// topnav logo + search + settings, and the page-bottom footer with store badges
// + links. The search/settings overlays are mounted once, globally.
// GO MODE IS FULL SCREEN, AND THE EXIT CONTROL IS THE ONLY WAY OUT (Randy's
// ruling 3, 2026-09-16). The page it names is the one page on the site with no
// site chrome: no header, no footer.
//
// WHY THIS IS THE LAYOUT'S JOB. `mapRootStyle` makes a maximized map a fixed,
// full-viewport overlay at MAXIMIZED_Z, but the header is fixed too and it
// wins: the map is nested inside page containers whose own stacking contexts
// scope it, so the header paints over the map's top-right corner and TAKES THE
// CLICKS — the contract button was measured at (1144, 12) with Settings at
// (1150, 11), and Playwright reported the gear icon intercepting every attempt
// to press it. Only the layout owns the header, so only the layout can fix it.
//
// Mantine's own `collapsed` is the switch: it drops the header AND zeroes the
// main area's top offset, so the map starts at the top of the viewport instead
// of 56 px down with a 56 px hole under it. ExploreMap's column then measures
// from a map root that IS the viewport, which is what moves the controls up.
//
// TWO WAYS IN, ONE MECHANISM (Randy, 2026-09-17: "expanded maps are chromeless
// like Go Mode").
//
//   * BY PAGE. Go Mode is a page and is always chromeless, so it is named here.
//   * BY STATE. A maximized map is not a page — it is ExploreMap state that
//     toggles without a navigation, on the home map, `/explore`, the station
//     panel and a chart page alike. The map publishes it through
//     `_chromeless.jsx` and this reads it back.
//
// The first answer used to be the ONLY answer, and it was the bug: every
// maximized map kept a header it had to dodge, so `controlColumnTop` dropped
// the whole control column 56 px on four surfaces while `/go` alone sat at 12.
const CHROMELESS_PAGES = new Set(['go/index'])

function Shell({ children }) {
  // `component` is the Inertia page name, so this survives every locale prefix
  // (/es/go renders the same component) without parsing a URL.
  const { component, props } = usePage()
  // `null` until a map says otherwise, which is the whole first-paint window on
  // a direct hit of `/explore`: the page is SERVER-RENDERED with the map
  // already expanded (Web::PagesController#explore renders home/index with
  // `expanded: true`) and LazyMap holds a full-screen skeleton until the
  // maplibre chunk lands. Without this seed the header would paint, sit over
  // that skeleton for as long as the chunk takes, and then disappear. Once any
  // map has reported, the map owns the answer — which is what brings the header
  // back when the reader collapses, since Inertia's props never change on the
  // in-place collapse (`preserveState`, see _expandUrl.js).
  const mapMaximized = useChromeless()
  const chromeless = CHROMELESS_PAGES.has(component) || (mapMaximized ?? props?.expanded === true)
  const { t, locale } = useI18n()
  const localePath = useLocalePath()
  const iosBadge = storeBadge('ios', locale, 44)
  const androidBadge = storeBadge('android', locale, 44)
  const [searchOpened, searchCtrl] = useDisclosure(false)
  const [settingsOpened, settingsCtrl] = useDisclosure(false)
  // ⌘K / Ctrl-K opens search anywhere; "/" opens it too (ignored while typing).
  useHotkeys([
    ['mod+K', searchCtrl.open],
    ['/', searchCtrl.open],
  ])

  return (
    <>
      <a href="#main-content" className="skip-link">
        {t('skip_to_content')}
      </a>
      <SearchOverlay opened={searchOpened} onClose={searchCtrl.close} />
      <SettingsOverlay opened={settingsOpened} onClose={settingsCtrl.close} />
      <AppShell
        header={{ height: `calc(${HEADER_CONTENT_H}px + ${SAFE_TOP})`, collapsed: chromeless }}
        padding={0}
      >
        {/* NOT RENDERED, not merely translated off-screen. `collapsed` above
            already zeroes the main area's offset; leaving the element in the
            DOM would keep a logo and two buttons in the tab order and in the
            accessibility tree of a screen nothing can reach them on. */}
        {!chromeless && (
          <AppShell.Header
            style={{
              background: NAV_BG,
              color: '#fff',
              borderBottom: NAV_BORDER,
              paddingTop: SAFE_TOP,
              paddingLeft: SAFE_LEFT,
              paddingRight: SAFE_RIGHT,
            }}
          >
            <Group h="100%" px="md" justify="space-between" wrap="nowrap">
              <Anchor component={Link} href={localePath('/')} underline="never" c="#fff">
                <Group gap={8} wrap="nowrap">
                  {/* 60px source for a 30px box: 2x for retina and nothing more.
                  The 1024x1024 master this used to point at was 49KB on every
                  page for a 30px slot — Lighthouse counted the whole thing as
                  waste. tides-logo.png is still the master; the sized copies
                  are generated from it (sips -Z 60 / -Z 144). */}
                  <img
                    src="/tides-logo-60.png"
                    alt=""
                    width={30}
                    height={30}
                    style={{ borderRadius: 7, display: 'block' }}
                  />
                  <Text fw={700} size="lg" c="#fff">
                    {t('app_name')}
                  </Text>
                </Group>
              </Anchor>
              <Group gap="lg" wrap="nowrap" align="center">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="lg"
                  onClick={searchCtrl.open}
                  aria-label={t('search_stations')}
                  title={t('search_stations_hint')}
                  style={{ '--ai-color': '#fff' }}
                >
                  <SearchIcon />
                </ActionIcon>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="lg"
                  onClick={settingsCtrl.open}
                  aria-label={t('settings')}
                  title={t('settings')}
                  style={{ '--ai-color': '#fff' }}
                >
                  <GearIcon />
                </ActionIcon>
              </Group>
            </Group>
          </AppShell.Header>
        )}

        <AppShell.Main id="main-content" tabIndex={-1} style={{ outline: 'none' }}>
          {/* Wrap only the page content, so the nav + footer survive a page
                error and the user can still navigate away (the boundary resets
                itself on Inertia navigation). */}
          <ErrorBoundary>{children}</ErrorBoundary>

          {/* The footer goes with the header. A chromeless page is a
              full-screen instrument — Go Mode, or any map the reader has
              maximized — and the map covers the viewport, so store badges and
              link columns scrolled underneath it would only be reachable by
              accident. */}
          {!chromeless && (
            <Box
              component="footer"
              style={{
                background: NAV_BG,
                color: '#fff',
                borderTop: NAV_BORDER,
                paddingBottom: SAFE_BOTTOM,
                paddingLeft: SAFE_LEFT,
                paddingRight: SAFE_RIGHT,
              }}
            >
              <Container size="md" py="xl">
                <Stack align="center" gap="lg">
                  <Group gap="md" justify="center" wrap="wrap">
                    {/* storeBadge() spreads src + width + height together, and it
                      has to stay that way. With only a height the box has no
                      width until the file loads and reveals its aspect ratio —
                      the badges snap from 0 to full width and the footer
                      reflows, which Lighthouse measured as the homepage's
                      largest layout shift (0.098, root cause "media element
                      lacking an explicit size"). Now that the artwork is
                      localized the widths are per-language, so they cannot be
                      written here as constants. */}
                    <Anchor
                      href={storeUrl('ios', locale)}
                      target="_blank"
                      aria-label={t('app_store')}
                      onClick={() => trackStoreClick('ios', 'footer')}
                    >
                      <img
                        src={iosBadge.src}
                        alt={t('app_store')}
                        width={iosBadge.width}
                        height={iosBadge.height}
                        style={{ display: 'block' }}
                      />
                    </Anchor>
                    <Anchor
                      href={storeUrl('android', locale)}
                      target="_blank"
                      aria-label={t('google_play')}
                      onClick={() => trackStoreClick('android', 'footer')}
                    >
                      <img
                        src={androidBadge.src}
                        alt={t('google_play')}
                        width={androidBadge.width}
                        height={androidBadge.height}
                        style={{ display: 'block' }}
                      />
                    </Anchor>
                  </Group>
                  <Group gap="xs" justify="center">
                    <Anchor
                      component={Link}
                      href={localePath('/guides')}
                      size="sm"
                      c="rgba(255,255,255,0.75)"
                    >
                      {t('guides')}
                    </Anchor>
                    <Text c="rgba(255,255,255,0.4)">·</Text>
                    <Anchor
                      component={Link}
                      href={localePath('/privacy')}
                      size="sm"
                      c="rgba(255,255,255,0.75)"
                    >
                      {t('privacy')}
                    </Anchor>
                    <Text c="rgba(255,255,255,0.4)">·</Text>
                    <Anchor
                      component={Link}
                      href={localePath('/terms')}
                      size="sm"
                      c="rgba(255,255,255,0.75)"
                    >
                      {t('terms')}
                    </Anchor>
                    <Text c="rgba(255,255,255,0.4)">·</Text>
                    <Anchor
                      component={Link}
                      href={localePath('/data-sources')}
                      size="sm"
                      c="rgba(255,255,255,0.75)"
                    >
                      {t('data_sources')}
                    </Anchor>
                    <Text c="rgba(255,255,255,0.4)">·</Text>
                    <Anchor
                      component={Link}
                      href={localePath('/support')}
                      size="sm"
                      c="rgba(255,255,255,0.75)"
                    >
                      {t('support')}
                    </Anchor>
                  </Group>
                  <LanguageSwitcher color="rgba(255,255,255,0.75)" />
                  <Text c="rgba(255,255,255,0.5)" size="sm">
                    © {new Date().getFullYear()} Shelter Island Mapping Company, LLC
                  </Text>
                </Stack>
              </Container>
            </Box>
          )}
        </AppShell.Main>
      </AppShell>
    </>
  )
}

// Site shell shared by every Inertia page (default Inertia layout, see
// entrypoints/inertia.jsx). Providers wrap the inner Shell so the nav + footer
// (and every page) can use useI18n / useFormat / useStations.
export default function Layout({ children }) {
  return (
    <MantineProvider defaultColorScheme="auto" theme={theme} deduplicateCssVariables={false}>
      <LocaleProvider>
        <UnitsProvider>
          <StationsProvider>
            {/* Outside Shell and inside everything else: Shell reads it, and
                the page below writes it. `children` is created out here, so a
                maximize re-renders Shell (which consumes the state) and bails
                out of the page subtree (which does not). */}
            <ChromelessProvider>
              <Shell>{children}</Shell>
            </ChromelessProvider>
          </StationsProvider>
        </UnitsProvider>
      </LocaleProvider>
    </MantineProvider>
  )
}
